/**
 * plugins/icons/pit.js
 *
 * Produuz.it's own icon set, registered next to MDI as the `pit` set so a glyph
 * is addressed as `pit:batch`. MDI stays the default set and every existing
 * `mdi-*` name keeps working untouched — see `docs/icon-libraries-comparison.md`
 * for why we draw a handful of glyphs instead of switching library.
 *
 * Drawing rules, so a custom glyph sits next to the other 267 MDI ones without
 * looking foreign:
 *
 *   - 24×24 viewBox, filled paths, no strokes — MDI's grid and style.
 *   - Structure (frames, rails, posts) is 2 units thick, the same weight as
 *     MDI's own bars. Contents (panels) are wider than the structure.
 *   - Gaps are at least 2 units. A v-icon at `size="small"` is 18 px, so one
 *     grid unit is 0.75 px and anything thinner than 2 units closes up.
 */

import { defineComponent, h, mergeProps } from 'vue'

/**
 * Batch — a rack seen from the front: an inverted T (post on a base rail) with
 * the panels of the batch leaning on the post from both sides.
 *
 * It is deliberately built on the same base rail as `mdi-bookshelf` (Rack,
 * icons.md §4), which is a bar at y 19→21 spanning x 3→21, so the two read as
 * a family. The post is what tells them apart: a Rack is loose slabs on a bar,
 * a Batch is panels resting against a post.
 *
 * The panels lean rather than standing upright. Four evenly spaced uprights
 * were tried first and read as a bar chart at 18 px; leaning them is what MDI
 * itself does with the third slab of `mdi-bookshelf`, and it is what makes the
 * glyph read as objects instead of data.
 */
const PATHS = {
    // Empty — the bare frame, nothing on it.
    'batch': 'M11 4H13V19H21V21H3V19H11V4Z',

    // Full — the same frame carrying two panels.
    'batch-full': 'M11 4H13V19H21V21H3V19H11V4Z'
        + 'M6 6H9L6.5 19H3.5Z'
        + 'M15 6H18L20.5 19H17.5Z',

    // Branch geometry — see the block comment below.
    'timber': 'M2.5 6.52L8.4 3.11L21.5 10.68L21.5 17.48L15.6 20.89L2.5 13.32Z'
        + 'M5.5 6.52L15.6 12.35L18.5 10.68L8.4 4.84Z'
        + 'M20 13.27L17.1 14.95L17.1 18.29L20 16.62Z'
        + 'M4 12.46L14.1 18.29L14.1 14.95L4 9.11Z',

    'plate': 'M2.5 9.38L12 3.89L21.5 9.38L21.5 14.62L12 20.11L2.5 14.62Z'
        + 'M5.5 9.38L12 13.13L18.5 9.38L12 5.62Z'
        + 'M20 11.97L13.5 15.73L13.5 17.51L20 13.76Z'
        + 'M4 13.76L10.5 17.51L10.5 15.73L4 11.97Z',

    // Assembly — a timber frame in elevation. See the block comment below.
    'assembly': 'M1 5L23 5L23 19L1 19Z'
        + 'M3 17L21 17L21 7L3 7Z'
        + 'M6 7L8 7L8 17L6 17ZM11 7L13 7L13 17L11 17ZM16 7L18 7L18 17L16 17Z'
        + 'M8 9.2L11 9.2L11 10.5L8 10.5Z'
        + 'M18 9.2L21 9.2L21 10.5L18 10.5ZM18 13.2L21 13.2L21 14.5L18 14.5Z',

    // Production — a nail gun with an inclined stick magazine. See below.
    'production': 'M2 4.2L2.43 2.6L3.6 1.43L5.2 1L14.6 1L15.05 1.12L15.38 1.45'
        + 'L15.5 1.9L15.5 8.1L15.38 8.55L15.05 8.88L14.6 9L5.2 9'
        + 'L3.6 8.57L2.43 7.4L2 5.8Z'
        + 'M15.5 2L17.6 3.95L22 3.95L22 6.05L17.6 6.05L15.5 8Z'
        + 'M6 8.5L10.5 8.5L10.5 18L6 18Z'
        + 'M18.1 5.8L21.6 5.8L11.32 23.6L7.82 23.6Z'
        + 'M4.5 18.7L4.66 18.1L5.1 17.66L5.7 17.5L10.7 17.5L11.3 17.66L11.74 18.1'
        + 'L11.9 18.7L11.74 19.3L11.3 19.74L10.7 19.9L5.7 19.9L5.1 19.74L4.66 19.3Z'
        + 'M11 9.24L12.6 11.44L11.6 12.16L10 9.96Z',

    // Module — a room-sized box, a door on one face and a window on the other.
    'module': 'M2.5 7.17L10.1 2.79L21.5 9.37L21.5 16.83L13.9 21.21L2.5 14.63Z'
        + 'M5.5 7.17L13.9 12.02L18.5 9.37L10.1 4.52Z'
        + 'M20 11.97L15.4 14.62L15.4 18.62L20 15.96Z'
        + 'M4 13.77L12.4 18.62L12.4 14.62L4 9.77Z'
        + 'M19.98 15.17L15.42 17.8L15.42 14.37L19.98 11.74Z'
        + 'M19.08 13.3L16.32 14.89L16.32 16.24L19.08 14.65Z'
        + 'M6.15 11.37L9.57 13.34L9.57 18.71L6.15 16.74Z'
        + 'M7.05 16.22L8.67 17.15L8.67 13.86L7.05 12.93Z',
}

/**
 * Assembly and Module — the two constructibles, drawn the way the shop sees
 * them: an Assembly is a panel you look at flat, a Module is a volume you walk
 * into. So they are deliberately *not* the same construction.
 *
 *   assembly  elevation of a timber frame: top and bottom rail, end studs,
 *             three intermediate studs, and two of the four bays carry an
 *             opening. A door is a bay with a header; a window is a bay with a
 *             header *and* a sill. That pair of horizontal members is the only
 *             thing separating them, which is exactly how a frame drawing does
 *             it — not size, not fill.
 *
 *   module    the same isometric box as the branch glyphs at 1.5 : 1 : 0.85,
 *             wireframed, with a door outlined on the long face and a window on
 *             the short one. The openings are rings, not solid marks: a filled
 *             rectangle on a wireframe reads as a panel stuck to the wall.
 *
 * Both are generated. Ordering of the subpaths does not matter because the fill
 * is nonzero and every ring is wound against its parent.
 *
 * KNOWN LIMIT: `assembly` carries eight members in a 24-unit box, so below
 * about 20 px the bays merge and it reads as a striped rectangle rather than a
 * frame. It is honest at 24 px and up. If it has to live in a dense table row,
 * the fix is fewer bays, not thinner members.
 */

/**
 * Production — a nail gun with an inclined stick magazine, six solid masses:
 * housing, muzzle, grip, rounded butt, trigger and the diagonal magazine.
 *
 * The **diagonal is the whole icon**. A gun body alone is a drill or a hair
 * dryer; the slanted magazine is what names it, and it is the one part that
 * survives at 16 px, so it gets the boldest angle the grid allows.
 *
 * Three things are load-bearing and easy to break:
 *
 *   - The magazine's top is cut **horizontally**, not square to its own axis, so
 *     it welds flush along the underside of the barrel instead of touching it at
 *     one corner. A square cut leaves a step no matter how far the magazine is
 *     pushed. The barrel then protrudes a little past the magazine's end, which
 *     is what stops the two from reading as a single wedge.
 *   - The chamber's rear overhangs the grip by about four units. Less and the
 *     cylinder stops looking capped; the grip is what has to move, since the
 *     chamber is already against the left edge of the grid.
 *   - The pocket between grip and magazine is the negative space that gives the
 *     silhouette its shape. Nothing may close it.
 *   - The trigger hangs free in that pocket and must not touch the magazine —
 *     a line bridging the two reads as a strut, not a trigger.
 *
 * The magazine sits at **30° from vertical**, and the angle is not a free
 * choice: it is what sets how far the magazine's foot lands from the grip.
 * Its top is pinned under the barrel, so leaning it over drags the foot left
 * and into the grip. At 38° the two overlap by two units and the magazine stops
 * reading as a separate stick; below about 32° the foot clears the grip and a
 * gap opens along the whole length. Standing it up much further than 30° is the
 * opposite failure — magazine and grip become two legs and the tool reads as a
 * stool.
 *
 * Its tip runs 3.7 units below the butt. Getting there meant **lifting the rest
 * of the gun by two units**, not lengthening the magazine further: the tip was
 * already at y 23.6 and a shape touching the viewBox edge reads as clipped, so
 * with 0.4 left at the bottom the only room available was at the top. The body
 * now sits at y 1, and there is no more of either — going further means
 * shrinking the gun.
 *
 * Lengthening the magazine is not free either:
 * the top edge is pinned under the barrel, so every unit of extra length walks
 * the foot further left, back towards the grip. That is the trade this glyph
 * keeps hitting — top pinned, angle fixed, so length and clearance are the same
 * dial turned from opposite ends.
 *
 * The butt and the compression chamber are rounded rather than cut square. The
 * chamber is rounded **asymmetrically** — a big radius at the rear, where the
 * cylinder is capped, and a small one at the front, where the barrel comes out
 * and a soft corner would leave a notch against it. Rounding it evenly loses
 * that reading and makes the housing look like a pill.
 *
 * The barrel is thin and meets the chamber through a **chamfered shoulder**
 * rather than a step, so the two are one part that narrows rather than two
 * boxes stacked.
 *
 * All corners are quarter arcs at three segments each, which is
 * indistinguishable from a true arc at these sizes and keeps the path short.
 *
 * Solid, not outline, unlike the rest of the set. An outlined nail gun has six
 * rings competing in a 24-unit box and turns to mush below 24 px — the same
 * wall `assembly` ran into. A nail-strip slot down the magazine was drawn and
 * dropped for the same reason: it reads at 44 px and hollows the magazine at
 * 18, which costs the silhouette that makes the glyph work.
 *
 * Replaces `mdi-floor-plan`, which drew the drawing rather than the work.
 */

/**
 * Timber and plate branches — the same box as `mdi-cube`, in the proportions
 * that define each branch. A branch is a family of shapes, so its glyph has to
 * say what the geometry is, not what the material grows on.
 *
 *   timber  one horizontal dimension much longer than the other two — a beam.
 *   plate   the vertical dimension much smaller than the other two — a sheet.
 *
 * Both are generated, not drawn by hand: a true 30° isometric projection of a
 * box, silhouette minus an inset copy of each of the three visible faces. That
 * is exactly how `mdi-cube-outline` is built — the same projection and the same
 * ~1.5-unit edge — which is what makes the three read as one family and lets
 * the proportion carry the whole meaning.
 *
 * The proportions are as extreme as 16 px allows and no more. A longer beam
 * (2.45 : 0.72) and a thinner plate (1.15 : 0.42) look better at 44 px and fall
 * apart in a table row: the faces close up under the edge inset, and the
 * generator drops the ones that no longer fit, which loses the wireframe.
 *
 * This replaces `mdi-cylinder` for timber and `mdi-layers-triple-outline` for
 * plate. Both were compromises: MDI's only free extrusion-like solid, and a
 * glyph that is really Zones (icons.md §2).
 */

/**
 * Mirrors Vuetify's own `VSvgIcon` (vuetify/lib/composables/icons.js), which is
 * not exported from any public entry point so it cannot be imported. The markup
 * has to match: `v-icon__svg` is what gives the path its size and `currentColor`
 * fill from the surrounding `v-icon`.
 */
const PitIcon = defineComponent({
    name: 'PitIcon',
    inheritAttrs: false,
    props: {
        icon: { type: String, default: '' },
        tag: { type: [String, Object, Function], required: true },
    },
    setup(props, { attrs }) {
        return () => {
            const path = PATHS[props.icon]
            if (!path && import.meta.env.DEV) {
                console.warn(`[pit icons] unknown icon "pit:${props.icon}". `
                    + `Known: ${Object.keys(PATHS).join(', ')}`)
            }
            return h(props.tag, mergeProps(attrs, { style: null }), {
                default: () => [
                    h('svg', {
                        class: 'v-icon__svg',
                        xmlns: 'http://www.w3.org/2000/svg',
                        viewBox: '0 0 24 24',
                        role: 'img',
                        'aria-hidden': 'true',
                    }, [h('path', { d: path ?? '' })]),
                ],
            })
        }
    },
})

export const pit = { component: PitIcon }

/** Exported for the icon lab in `docs/` and for tests — not needed to render. */
export const PIT_PATHS = PATHS
